<template>
  <div class="grid grid-cols-4 gap-6">
    <div
      v-for="resource in resources"
      :key="resource.id"
      :data-testid="`resource-card-${resource.id}`"
      class="flex h-full flex-col rounded-sm border border-subtle bg-surface-elevation-1 p-4"
    >
      <div>
        <div class="txt-body-m-bold">{{ resource.title }}</div>
        <img
          :src="resource.src"
          class="justify-self-center pt-6 pb-12"
          :class="{
            'w-2/3': imagePreviewSize === 's',
            'opacity-50': isFenced,
            'pointer-events-none': isFenced,
          }"
        />
      </div>
      <div class="mt-auto flex flex-col gap-2">
        <div class="txt-body-s-bold">{{ t('Reviews.shared.media.widgets.htmlCodeTitle') }}</div>
        <div class="flex rounded-sm border border-subtle bg-surface p-1">
          <div class="mr-2 scrollbar-hide w-64 overflow-scroll whitespace-nowrap text-subtlest" :class="{ 'cursor-not-allowed': isFenced }">
            {{ isFenced ? `${resource.link.substring(0, 24)}...` : resource.link }}
          </div>
          <div v-if="isFenced">
            <CopyIcon class="mt-1 lucide-icon-s cursor-not-allowed align-middle text-icon-disabled" />
          </div>
          <TooltipWrapper v-else top>
            <template #trigger>
              <CopyIcon
                class="mt-1 lucide-icon-s cursor-pointer align-middle text-icon-primary hover:text-icon-selected"
                :class="{ '!text-icon-disabled': isFenced }"
                @click="copyURLToClipboard(resource.link)"
              />
            </template>
            <template #content>
              <div v-html="t('Reviews.shared.media.widgets.embedToolTip')"></div>
            </template>
          </TooltipWrapper>
        </div>
        <div v-if="showDownload">
          <div class="pt-2 txt-body-s-bold">{{ t('Reviews.shared.media.widgets.downloadSelectTitle') }}</div>
          <DropDownSelect class="flex w-full rounded-sm bg-surface text-subtlest">
            <template #activator="{ open }">
              <button
                class="flex w-full items-center justify-between rounded-sm border border-subtle p-1"
                :class="{ 'cursor-not-allowed': isFenced }"
                :disabled="isFenced"
                @click.prevent="open"
              >
                <span>
                  {{ selectedOptions[resource.id]?.text }}
                </span>
                <ChevronDownIcon class="lucide-icon-s text-icon-subtlest" />
              </button>
            </template>
            <template #default="{ close }">
              <ul class="m-0 list-none p-0">
                <li v-for="option in downloadOptions" :key="`${option.text}-items-per-page`">
                  <label class="block cursor-pointer">
                    <input
                      :name="`option-assigned-${resource.id}`"
                      type="radio"
                      :value="option"
                      class="peer pointer-events-none absolute opacity-0"
                      :checked="selectedOptions[resource.id] === option"
                      @change="setDownloadParams(resource.id, option)"
                    />
                    <span class="block min-w-20 px-3 py-2 peer-checked:bg-surface-elevation-1 hover:bg-surface-elevation-1" @click="close">
                      {{ option.text }}
                    </span>
                  </label>
                </li>
              </ul>
            </template>
          </DropDownSelect>
          <button v-if="isFenced" class="btn-primary-color-s mt-2 w-full" disabled>
            {{ t('Reviews.shared.media.widgets.downloadButtonLabel') }} <DownloadIcon class="ml-2 lucide-icon-m" />
          </button>
          <a
            v-else
            class="btn-primary-color-s mt-2 w-full"
            :href="getDownloadLink(resource, selectedOptions[resource.id])"
            :download="getDownloadFilename(resource.title, resource.id)"
            target="_blank"
            @click="trackResourceDownload"
          >
            {{ t('Reviews.shared.media.widgets.downloadButtonLabel') }} <DownloadIcon class="ml-2 lucide-icon-m" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { DownloadIcon, CopyIcon, ChevronDownIcon } from '@ramp106/omrjs-core-ui/icons'
import { appendQuery } from '@ramp106/omrjs-routing'
import { track, type EntityType } from '@ramp106/omrjs-tracking-reviews'
import { ref, watchEffect, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { showNotification } from '@/helpers/notificationHelper'

interface Resource {
  id: string
  title: string
  src: string
  link: string
}

const { t } = useI18n()

const props = defineProps<{
  resources: Array<Resource>
  imagePreviewSize: 's' | 'm'
  entityType: EntityType
  entityId: string
  resourceType: 'widget' | 'badge'
  showDownload?: boolean
  isFenced?: boolean
}>()

function resourceEventPayload() {
  return {
    surface: 'widget' as const,
    entity_type: props.entityType,
    entity_id: props.entityId,
    resource_type: props.resourceType,
  }
}

function trackResourceDownload() {
  track('resource_download', resourceEventPayload())
}

interface DownloadOption {
  text: string
  size: number
  format: 'svg' | 'png'
}

const downloadOptions: DownloadOption[] = [
  { text: 'SVG', size: 500, format: 'svg' },
  { text: 'PNG 500px', size: 500, format: 'png' },
  { text: 'PNG 1000px', size: 1000, format: 'png' },
  { text: 'PNG 2000px', size: 2000, format: 'png' },
]

const selectedOptions = ref<Record<string, DownloadOption>>({})

function setDownloadParams(badgeId: string, option: DownloadOption) {
  selectedOptions.value[badgeId] = option
}

function initSelectedOptions(resources: Array<Resource>, refObj: Ref<Record<string, DownloadOption>>) {
  resources.forEach((resource) => {
    if (!refObj.value[resource.id]) {
      refObj.value[resource.id] = downloadOptions[0]
    }
  })
}

function copyURLToClipboard(link: string) {
  track('resource_embed_copy', resourceEventPayload())
  navigator.clipboard.writeText(link)
  showNotification(t('Reviews.shared.media.widgets.embedCopied'), 'success')
}

function getDownloadLink(badge: { src: string }, option: DownloadOption) {
  if (!option) return ''
  const [url, existingQuery] = badge.src.split('?')
  // agency widgets carry the template as `?template=dark` — keep it, or downloads lose the variant
  const params: Record<string, string> = Object.fromEntries(new URLSearchParams(existingQuery ?? ''))
  if (option.format === 'svg') {
    return appendQuery(url, { ...params, format: 'svg' })
  } else if (option.format === 'png') {
    return appendQuery(url, { ...params, width: option.size.toString(), format: 'png' })
  }
  return url
}

function getDownloadFilename(badgeTitle: string, resourceId: string) {
  return badgeTitle.toLowerCase().replace(' ', '') + '.' + selectedOptions.value[resourceId].format
}

watchEffect(() => {
  initSelectedOptions(props.resources, selectedOptions)
})
</script>
